import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import SurveySectionTabs, { SURVEY_TAB_DASHBOARD } from "../components/SurveySectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const { useAuth } = require("../contexts/AuthContext");
const ErrorBanner = require("../components/ErrorBanner").default;

function ResponseRateGauge({ value }) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  const r = 42;
  const cx = 50;
  const cy = 50;
  const rad = (Math.PI * pct) / 100;
  const endX = cx - r * Math.cos(rad);
  const endY = cy - r * Math.sin(rad);
  const needleLen = 36;
  const needleX = cx - needleLen * Math.cos(rad);
  const needleY = cy - needleLen * Math.sin(rad);
  return (
    <svg width="64" height="40" viewBox="0 0 100 55" style={{ display: "block", flexShrink: 0 }}>
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#e5e7eb" strokeWidth="8" strokeLinecap="round" />
      {pct > 0 && <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${endX} ${endY}`} fill="none" stroke="#0d9488" strokeWidth="8" strokeLinecap="round" />}
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#374151" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="4" fill="#fff" stroke="#e5e7eb" strokeWidth="1" />
    </svg>
  );
}

function SurveyResultsList() {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // '' = all, 'Open', 'Closed'

  const loadSurveys = useCallback(() => {
    if (!account_id) return;
    setLoading(true);
    setError("");
    const body = { account_id };
    if (statusFilter === "Open" || statusFilter === "Closed") body.status = statusFilter;
    apiFetch(serverUrl + "/getSurveys", {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((resp) => resp.json())
      .then((data) => setSurveys(Array.isArray(data) ? data : []))
      .catch((err) => setError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl, statusFilter]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const today = dayjs().startOf("day");
  const responseRatePct = (invited, completed) => {
    if (invited == null || invited === 0) return 0;
    return Math.round((Number(completed) / Number(invited)) * 100);
  };
  const daysElapsed = (startDate) => {
    if (!startDate) return null;
    const d = dayjs(startDate).startOf("day");
    return today.diff(d, "day");
  };
  const daysLeft = (endDate) => {
    if (!endDate) return null;
    const d = dayjs(endDate).startOf("day");
    return d.diff(today, "day");
  };
  const isExpired = (s) => (s.status || "Open").toLowerCase() === "open" && s.end_date && dayjs(s.end_date).startOf("day").isBefore(today);
  const totalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    return dayjs(endDate).startOf("day").diff(dayjs(startDate).startOf("day"), "day");
  };

  // Summary stats from full survey list (before search filter)
  const totalSurveys = (surveys || []).length;
  const openSurveys = (surveys || []).filter((s) => (s.status || "Open").toLowerCase() === "open");
  const expiredCount = openSurveys.filter((s) => isExpired(s)).length;
  const openCount = openSurveys.filter((s) => !isExpired(s)).length; // OPEN = active (open, not expired)
  const closedCount = (surveys || []).filter((s) => (s.status || "").toLowerCase() === "closed").length;
  const totalCompleted = (surveys || []).reduce((sum, s) => sum + (Number(s.completed) || 0), 0);
  const totalInvited = (surveys || []).reduce((sum, s) => sum + (Number(s.invited) || 0), 0);
  const avgResponseRate = totalInvited > 0 ? Math.round((totalCompleted / totalInvited) * 100) : null;

  const filtered = (surveys || []).filter((s) => {
    const search = (searchTerm || "").trim().toLowerCase();
    if (!search) return true;
    return (
      (s.name && s.name.toLowerCase().includes(search)) ||
      (s.description && s.description.toLowerCase().includes(search)) ||
      (s.status && s.status.toLowerCase().includes(search))
    );
  });

  const sorted = filtered;

  return (
    <div className="page-container page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Survey results and related views in one place.">
        <SurveySectionTabs activeTab={SURVEY_TAB_DASHBOARD} />
      </SectionTabsPageHeader>
      <div className="page-content">

        <ErrorBanner message={error} onDismiss={() => setError("")} />

        <div className="toolbar">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, description…"
            className="input input-min-200"
          />
          <span className="toolbar-label">Status:</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input input-min-120">
            <option value="">All</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
          </select>
          <span className="toolbar-divider" />
          <button type="button" onClick={() => { setSearchTerm(""); setStatusFilter(""); }} className="btn btn-secondary">Clear</button>
        </div>

      {/* KPI cards */}
      {!loading && surveys.length > 0 && (
        <div className="page-section" style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "12px" }}>
          <div className="panel">
            <div className="panel-hint">TOTAL SURVEYS</div>
            <div className="panel-title">{totalSurveys}</div>
          </div>
          <div className="panel">
            <div className="panel-hint">OPEN</div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="panel-title">{openCount}</span>
              {openCount > 0 && <span className="badge" style={{ background: "#22c55e", width: 8, height: 8, padding: 0, borderRadius: "50%" }} aria-hidden="true" />}
            </div>
          </div>
          <div className="panel">
            <div className="panel-hint">EXPIRED</div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="panel-title" style={{ color: "#dc2626" }}>{expiredCount}</span>
              {expiredCount > 0 && <span className="badge" style={{ background: "#dc2626", width: 8, height: 8, padding: 0, borderRadius: "50%" }} aria-hidden="true" />}
            </div>
          </div>
          <div className="panel">
            <div className="panel-hint">CLOSED</div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="panel-title">{closedCount}</span>
              {closedCount > 0 && <span className="badge" style={{ background: "#6b7280", width: 8, height: 8, padding: 0, borderRadius: "50%" }} aria-hidden="true" />}
            </div>
          </div>
          <div className="panel">
            <div className="panel-hint">TOTAL COMPLETED RESPONSES</div>
            <div className="panel-title">{totalCompleted}</div>
          </div>
          <div className="panel" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="panel-hint">AVG. RESPONSE RATE</div>
              <div className="panel-title">{avgResponseRate != null ? `${avgResponseRate}%` : "—"}</div>
            </div>
            {avgResponseRate != null && <ResponseRateGauge value={avgResponseRate} />}
          </div>
        </div>
      )}

      {/* Status Timeline + Participant Engagement */}
      {!loading && sorted.length > 0 && (
        <div className="page-section" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
          <div className="panel">
            <div className="panel-row" style={{ flexWrap: "wrap", marginBottom: 8 }}>
              <span className="panel-title" style={{ marginBottom: 0 }}>Status Timeline</span>
              <span className="panel-hint" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: "#0d9488" }} /> Day elapsed
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: "#e5e7eb" }} /> Remaining
                </span>
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sorted.slice(0, 8).map((s) => {
                const total = totalDays(s.start_date, s.end_date);
                const elapsed = total > 0 ? Math.min(total, Math.max(0, (daysElapsed(s.start_date) ?? 0))) : 0;
                const remaining = total > 0 ? total - elapsed : 0;
                const expired = isExpired(s);
                const left = daysLeft(s.end_date);
                return (
                  <div key={s._id} className="panel-row" style={{ minHeight: 28, flexWrap: "nowrap" }}>
                    <button type="button" onClick={() => navigate(`/Surveys/${s._id}/details`)} className="btn-link" style={{ flexShrink: 0, width: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }} title="View survey details">
                      {s.name}
                    </button>
                    <div style={{ flex: 1, minWidth: 80, display: "flex", height: 24, borderRadius: 4, overflow: "hidden", background: "#f3f4f6" }}>
                      {total > 0 ? (
                        <>
                          <div style={{ width: `${(elapsed / total) * 100}%`, minWidth: elapsed > 0 ? 24 : 0, height: "100%", background: "#0d9488", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#fff" }}>
                            {elapsed > 0 ? elapsed : ""}
                          </div>
                          <div style={{ flex: 1, minWidth: remaining > 0 ? 24 : 0, height: "100%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#6b7280" }}>
                            {remaining > 0 ? remaining : left != null && left < 0 ? left : ""}
                          </div>
                        </>
                      ) : (
                        <div style={{ width: "100%", height: "100%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#6b7280" }}>—</div>
                      )}
                    </div>
                    {expired && <span className="badge" style={{ background: "#dc2626", color: "#fff" }}>EXPIRED</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="panel">
            <div className="panel-title" style={{ marginBottom: 10 }}>Participant Engagement</div>
            <div className="table-scroll">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Survey</th>
                      <th style={{ textAlign: "right" }}>Invited</th>
                      <th style={{ textAlign: "right" }}>Completed</th>
                      <th style={{ textAlign: "right" }}>Response Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s) => {
                      const pct = responseRatePct(s.invited, s.completed);
                      return (
                        <tr key={s._id}>
                          <td>
                            <button type="button" onClick={() => navigate(`/Surveys/${s._id}/details`)} className="btn-link" title="View survey details">{s.name}</button>
                          </td>
                          <td style={{ textAlign: "right" }}>{s.invited ?? 0}</td>
                          <td style={{ textAlign: "right" }}>{s.completed ?? 0}</td>
                          <td style={{ textAlign: "right" }}>
                            {(s.invited || 0) > 0 ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                                <div style={{ width: 60, height: 8, borderRadius: 4, background: "#e5e7eb", overflow: "hidden" }}>
                                  <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: "#22c55e", borderRadius: 4 }} />
                                </div>
                                <span>{pct}%</span>
                              </div>
                            ) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <p className="empty-message">Loading surveys…</p>}
      {!loading && sorted.length === 0 && <p className="empty-message">{surveys.length === 0 ? "No surveys found." : "No surveys match your search."}</p>}
      </div>
    </div>
  );
}

export default SurveyResultsList;
